import React, { Component, MouseEvent } from 'react';
import { ColumnProps } from 'primereact/column';
import CustomTable from '../../custom/CustomTable';
import { CollectionListField, ListColumnField } from '../../../domain/list/ListDomains';
import CustomInput from '../../custom/CustomInput';
import CustomEmptyComponent from '../../custom/CustomEmptyComponent';
import { dispatch, RootState } from '../../../redux/store';
import { ListState } from '../../../redux/reducers/ListReducer';
import { changeList, changeListCreatedData, changeListData, changeListIsUpdate } from '../../../redux/actions/ListActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import ListDataService from '../../../service/ListDataService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { query, repository, subscribe } from '@trlogic/client-js';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import FilterParser from '../../../helpers/FilterParser';
import _ from 'lodash';
import { CustomFilterItemProps } from '../../custom/CustomFilterItem';
import DataType from '../../../constant/DataType';
import { generateUUIDV4 } from '../../../util/Generator';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import Icon from '../../../icons/Icon';
import RoleHelper from '../../../RoleHelper';
import CustomCheckBox from '../../custom/CustomCheckBox';
import { ComponentState, setSelectedItems } from '../../../redux/actions/ComponentActions';
import ReadOnlyQueryableRepository = repository.ReadOnlyQueryableRepository;
import LanguageUtil from '../../../util/LanguageUtil';
import ComponentName from '../../../constant/ComponentName';
import PendingCheckerRequestTracker from '../../../helpers/PendingCheckerRequestTracker';
import { ListHistoryMeta } from '../../../helpers/ListHistoryPreviewHelper';

class ListQueryDataViewComponent extends Component<Props, State> {
  public state: State = new State();

  private listDataService: ListDataService = ServiceContainer.getInstance().get(ServiceNames.LIST_DATA);

  private repository: ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  private collectionDataSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private pendingDeleteTracker = new PendingCheckerRequestTracker();

  render() {
    const { currentPage, rows, pendingDelete } = this.state;
    const {
      list: { data: liveData, list },
      component: { selectedComponentIds },
      historyMode,
      historyMeta,
    } = this.props;
    const data = historyMode ? liveData.slice((currentPage - 1) * rows, currentPage * rows) : liveData;
    const totalRecord = historyMode ? liveData.length : this.state.totalRecord;
    return (
      <div className={'flex w-full h-full flex-col gap-y-5 mt-5'}>
        <div className={'px-5 flex flex-col gap-y-5 w-full lg:!max-h-[calc(100vh-25rem)] overflow-y-scroll'}>
          {historyMode && (
            <div className={'flex flex-col gap-y-1'} data-testid='list_query_data_history_banner'>
              {historyMeta?.hasGap && (
                <div className={'text-xs text-amber-600'}>
                  {LanguageUtil.get({
                    id: 'components.list.history.hasGapWarning',
                    defaultMessage:
                      'This list has had CSV import(s) in its history; items added via CSV import may not appear in this historical view.',
                  })}
                </div>
              )}
              {historyMeta?.truncated && (
                <div className={'text-xs text-amber-600'}>
                  {LanguageUtil.get({
                    id: 'components.list.history.truncatedWarning',
                    defaultMessage: 'This list has a large history; this view may be incomplete.',
                  })}
                </div>
              )}
            </div>
          )}
          {list.fields.length == 0 || data.length == 0 ? (
            <div className={'mt-[100px]'}>
              <CustomEmptyComponent
                label={LanguageUtil.get({
                  id: 'common.label.startYourTransactionsByCreatingNewList',
                  defaultMessage: 'You can start your transactions by creating a new list.',
                })}
              />
            </div>
          ) : (
            <CustomTable
              columns={this.getColumns()}
              selection={selectedComponentIds}
              onSelectionChange={this.onSelectionChange}
              selectionMode='checkbox'
              value={data}
            />
          )}
        </div>
        <CustomPaginator
          currentPage={currentPage}
          currentRecords={data.length}
          rows={rows}
          totalRecords={totalRecord}
          rowsPerPageOptions={[5, 10, 20, 50, 100, 200]}
          onPageChange={this.onPageChange}
        />
      </div>
    );
  }

  public componentDidMount(): void {
    if (this.props.historyMode) {
      this.setState({ loading: false });
    } else {
      this.startTracker();
      const {
        list: { list },
      } = this.props;
      this.pendingDeleteTracker.start(ComponentName.LIST_DATA, list.name, (pendingDelete) => this.setState({ pendingDelete }));
    }
    dispatch(setSelectedItems([]));
  }

  public async componentWillUnmount() {
    await this.stopTracker();
    await this.pendingDeleteTracker.stop();
  }

  private startTracker = () => {
    const {
      list: { list },
    } = this.props;
    const { filters, rows, currentPage } = this.state;
    if (!list.name) return;
    let dataCollection: string = `list_${list.name}`;
    let dataQuery: query.SelectQuery = FilterParser.toQuery(
      _.cloneDeep(filters),
      query.QueryBuilder.select()
        .limit(rows)
        .skip((currentPage - 1) * rows)
        .from(dataCollection),
    ).build();
    this.collectionDataSubscription = subscribe.Formica.subscribe(dataCollection, dataQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.collectionDataSubscription]);
  };

  private tracker = async () => {
    const { changeListData } = this.props;
    const { filters, rows } = this.state;
    if (this.collectionDataSubscription?.ready()) {
      const {
        list: { list },
      } = this.props;
      const dataCollection: string = `list_${list.name}`;
      const dataQuery: query.SelectQuery = FilterParser.toQuery(_.clone(filters))
        .from(dataCollection)
        .limit(rows)
        .orderBy({
          field: 'createdAt',
          operator: 'DESC',
        })
        .build();
      const data = await this.repository.findMany<CollectionListField>(dataQuery);
      await this.prepareCount();
      changeListData(data);
      this.setState({ loading: false });
    }
  };

  private stopTracker = async () => {
    await this.collectionDataSubscription?.stop();
    await this.computation?.stop();
  };

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private getColumns = (): ColumnProps[] => {
    const {
      list: { list },
    } = this.props;
    const tableColumns: Array<object> = new Array<object>();
    list.fields.map((column) => {
      tableColumns.push({
        key: column.name,
        field: column.name,
        alignHeader: 'left',
        header: column.name,
        body: (record: Record<string, any>) => {
          return this.getValueComponent(column, record);
        },
      });
    });
    if (this.props.historyMode) {
      return tableColumns;
    }
    tableColumns.push({
      field: 'action',
      header: 'Action',
      className: 'max-w-[75px] 2xl:max-w-[100px]',
      sortable: false,
      body: (record: Record<string, any>) => {
        const { pendingDelete } = this.state;
        return (
          <div
            className={pendingDelete ? 'cursor-not-allowed' : undefined}
            onClick={(e: MouseEvent<HTMLElement>) => {
              if (pendingDelete) return;
              RoleHelper.canEdit('List') &&
                customConfirm({
                  target: e.currentTarget,
                  accept: () => {
                    this.removeCacheData(record);
                  },
                  message: 'Message',
                });
            }}
          >
            <Icon icon={'delete'} size={24} className={pendingDelete ? 'text-[#C0C7D1]' : undefined} />
          </div>
        );
      },
    });
    return tableColumns;
  };

  private getValueComponent = (column: ListColumnField, record: Record<string, any>) => {
    const isKeyEmpty = column.key && (record[column.name] == undefined || record[column.name].trim().length == 0);
    switch (column.type) {
      case DataType.NUMBER:
        return (
          <div className={'px-5'}>
            <CustomInput
              label={'input'}
              type='number'
              id={record[column.name]}
              inputType={!isKeyEmpty ? 'ghost' : 'default'}
              className={isKeyEmpty ? 'border-2 border-red-400' : ''}
              value={record[column.name]}
              disabled={true}
            />
          </div>
        );
      case DataType.STRING:
      case DataType.DATE:
        return (
          <div>
            <CustomInput
              label={'input'}
              type='text'
              inputType={!isKeyEmpty ? 'ghost' : 'default'}
              className={isKeyEmpty ? 'border-2 border-red-400' : ''}
              value={record[column.name]}
              id={record[column.name]}
              disabled={true}
            />
          </div>
        );
      case DataType.BOOLEAN:
        return (
          <div className='text-left'>
            <CustomCheckBox checked={record[column.name]} onChangeChecked={(checked, e) => {}} />
          </div>
        );
      default:
        return null;
    }
  };

  private onPageChange = (event: CustomPaginatorProps) => {
    if (this.props.historyMode) {
      this.setState({ rows: event.rows, currentPage: event.page });
      return;
    }
    this.setState({ rows: event.rows, currentPage: event.page }, this.resubscribeTracker);
  };

  private async prepareCount() {
    const { filters } = this.state;
    const {
      list: { list },
    } = this.props;
    const dataCollection: string = `list_${list.name}`;
    const query = FilterParser.toQuery(_.clone(filters)).from(dataCollection).build();
    const count = await this.listDataService.getCount(list.name, query);
    this.setState({ totalRecord: count });
  }

  private removeCacheData = async (record: Record<string, any>) => {
    if (record.id == undefined) return;
    const {
      list: { list },
    } = this.props;
    await this.listDataService.deleteListData(list.name, record.id);
  };
}

class State {
  currentPage: number = 1;
  rows = 10;
  totalRecord = 10;
  filters: Array<CustomFilterItemProps> = [
    {
      id: generateUUIDV4(),
      field: undefined,
      operator: '',
      value: '',
      logic: 'and',
    },
  ];
  loading: boolean = true;
  fileVisible: boolean = false;
  pendingDelete: boolean = false;
}

interface Props extends ReduxProps {
  historyMode?: boolean;
  historyMeta?: ListHistoryMeta;
}

const mapStateToProps = (state: RootState) => ({
  list: state.list as ListState,
  component: state.component as ComponentState,
});
const mapDispatchToProps = { changeList, changeListIsUpdate, changeListData, changeListCreatedData };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ListQueryDataViewComponent);
